// PO 2026-02 · Trade Setup — friendly required-field presentation helper.
// Maps backend readiness tokens (from _installed_selling_ready /
// _flat_price_ready / _materials_labor_ready / _time_materials_ready /
// _sub_markup_ready / _combination_ready / _generic_ready / _markup_ready
// in trade_setup_service.py) to contractor-facing labels in EN and ES.
// Never expose raw snake_case tokens.
//
// Consumed by TradeSetupPanel.jsx and CohortTradePanel.jsx.

const LABELS = {
  en: {
    pricing_method: "Pricing method",
    pricing_structure: "Pricing structure",
    material_source: "Material source",
    labor_approach: "Labor approach",
    markup_handling: "Markup handling",
    selling_price_per_unit: "Selling price per unit",
    selling_price_unit: "Selling price unit",
    flat_price: "Flat price",
    labor_amount: "Labor amount",
    material_amount: "Material amount",
    material_total: "Material total",
    markup_pct: "Markup %",
    material_markup_pct: "Material markup %",
    labor_markup_pct: "Labor markup %",
    margin_pct: "Margin %",
    overhead_pct: "Overhead %",
    profit_pct: "Profit %",
    overhead_amount: "Overhead amount",
    profit_amount: "Profit amount",
    markup_notes: "Markup notes",
    material_source_notes: "Material source notes",
    labor_notes: "Labor notes",
    combination_notes: "Combination pricing details",
    scope_defaults_reviewed_at: "Scope review",
    contractor_approved_at: "Approval",
    // Validation-shape tokens
    selling_price_unit_invalid: "Selling price unit (please pick a valid unit)",
    selling_price_per_unit_invalid: "Selling price per unit (please enter a number)",
    flat_price_invalid: "Flat price (please enter a number)",
    flat_price_negative: "Flat price (must be zero or greater)",
    zero_price_requires_confirmation: "Zero-price confirmation",
    markup_handling_must_be_built_in_for_installed_selling:
      "Markup handling (must be Built-in for All-in Price)",
    labor_approach_must_be_hourly_daily_or_per_job:
      "Labor approach (must be Hourly, Daily, or Per-job)",
    help_me_calculate_contact_pending:
      "Markup handling (JobClose AI will follow up)",
    help_me_contact_pending:
      "Pricing method (JobClose AI will follow up)",
  },
  es: {
    pricing_method: "Método de precios",
    pricing_structure: "Estructura de precios",
    material_source: "Fuente de materiales",
    labor_approach: "Enfoque de mano de obra",
    markup_handling: "Manejo del margen",
    selling_price_per_unit: "Precio de venta por unidad",
    selling_price_unit: "Unidad del precio de venta",
    flat_price: "Precio fijo",
    labor_amount: "Monto de mano de obra",
    material_amount: "Monto de materiales",
    material_total: "Total de materiales",
    markup_pct: "Margen %",
    material_markup_pct: "Margen de materiales %",
    labor_markup_pct: "Margen de mano de obra %",
    margin_pct: "Utilidad %",
    overhead_pct: "Gastos generales %",
    profit_pct: "Ganancia %",
    overhead_amount: "Monto de gastos generales",
    profit_amount: "Monto de ganancia",
    markup_notes: "Notas del margen",
    material_source_notes: "Notas de la fuente de materiales",
    labor_notes: "Notas de mano de obra",
    combination_notes: "Detalles de precios combinados",
    scope_defaults_reviewed_at: "Revisión del alcance",
    contractor_approved_at: "Aprobación",
    selling_price_unit_invalid:
      "Unidad del precio de venta (selecciona una unidad válida)",
    selling_price_per_unit_invalid:
      "Precio de venta por unidad (ingresa un número)",
    flat_price_invalid: "Precio fijo (ingresa un número)",
    flat_price_negative: "Precio fijo (debe ser cero o mayor)",
    zero_price_requires_confirmation: "Confirmación de precio cero",
    markup_handling_must_be_built_in_for_installed_selling:
      "Manejo del margen (debe ser Incluido para Precio Todo Incluido)",
    labor_approach_must_be_hourly_daily_or_per_job:
      "Enfoque de mano de obra (debe ser Por hora, Por día o Por trabajo)",
    help_me_calculate_contact_pending:
      "Manejo del margen (JobClose AI dará seguimiento)",
    help_me_contact_pending:
      "Método de precios (JobClose AI dará seguimiento)",
  },
};

export const friendlyFieldLabel = (token, lang = "en") => {
  const dict = LABELS[lang] || LABELS.en;
  if (!token) return "";
  // Return the mapped label, or a lightly-humanized fallback so a
  // newly-added token never leaks the raw snake_case form.
  if (dict[token]) return dict[token];
  return String(token).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

// Build the "Please complete the highlighted fields: X and Y." summary.
// Punctuation matches the PO-approved copy in EN and ES.
export const buildMissingSummary = (missing, lang = "en") => {
  const labels = (missing || []).map((t) => friendlyFieldLabel(t, lang)).filter(Boolean);
  if (!labels.length) return "";
  const unique = Array.from(new Set(labels));
  if (lang === "es") {
    const list = unique.length === 1
      ? unique[0]
      : unique.slice(0, -1).join(", ") + " y " + unique[unique.length - 1];
    return `Completa los campos resaltados: ${list}.`;
  }
  const list = unique.length === 1
    ? unique[0]
    : unique.slice(0, -1).join(", ") + " and " + unique[unique.length - 1];
  return `Please complete the highlighted fields: ${list}.`;
};

// Extract the missing[] array from a caught axios error. Returns [] if
// the error is not a system_setup_incomplete envelope.
export const extractMissingFromError = (e) => {
  const d = e && e.response && e.response.data && e.response.data.detail;
  if (d && typeof d === "object" && d.code === "system_setup_incomplete") {
    return Array.isArray(d.missing) ? d.missing.slice() : [];
  }
  return [];
};
